import React, { useEffect, useMemo, useState } from "react";
import { View, Text, StyleSheet, ActivityIndicator } from "react-native";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DataTable,
  Column,
} from "../../../components/adminDashboard/AICompanion";
import { colors, spacing } from "../../../styles/design-system";
import { Sparkle } from "@phosphor-icons/react";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { TopQuestionItem } from "@axio/graphql/generated/graphql";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
type QuestionType = "assignment" | "course";

type Question = {
  id: string;
  question: string;
  context: string;
  frequency: number;
  type: QuestionType;
};

type Filter = "all" | "assignment" | "course";

type Props = {
  questions: TopQuestionItem[];
  loading: boolean;
  onCountChange?: (count: number) => void;
};

export default function CommonQuestionsTab({ questions, loading, onCountChange }: Props) {
  const [filter, setFilter] = useState<Filter>("all");
  const questionsLoading = loading;

  const handleFilterChange = (value: string) => {
    if (value === "all" || value === "assignment" || value === "course") {
      setFilter(value);
    }
  };
  const mappedQuestions: Question[] = useMemo(() => {
    return questions.map((q) => ({
      id: `${q.courseId}-${q.assignmentId}-${q.questionText}`,
      question: q.questionText,
      context: q.scope === "assignment" ? `${q.courseName}: ${q.assignmentName}` : q.courseName || "Unknown course",
      frequency: q.frequency,
      type: q.scope === "assignment" ? "assignment" : "course",
    }));
  }, [questions]);

  const filteredQuestions = useMemo(() => {
    if (filter === "all") return mappedQuestions;
    return mappedQuestions.filter((q) => q.type === filter);
  }, [filter, mappedQuestions]);

  useEffect(() => {
    onCountChange?.(filteredQuestions.length);
  }, [filteredQuestions, onCountChange]);

  const isQuestionEmpty = !questionsLoading && filteredQuestions.length === 0;

  const columns: Column<Question>[] = [
    {
      key: "rank",
      flex: 0.08,
      renderCell: (_q, index) => (
        <View
          style={{
            alignItems: "flex-end",
          }}>
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: colors.grey[100],
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Text
              style={{
                fontSize: 12,
                fontWeight: "600",
                color: colors.grey[600],
              }}>
              {index + 1}
            </Text>
          </View>
        </View>
      ),
    },
    {
      key: "question",
      flex: 5,
      renderCell: (q) => (
        <View>
          <Text style={{ fontWeight: "600" }}>{q.question}</Text>
          <CustomSpacing type="vertical" size="xs" />
          <Text style={{ color: "#667085" }}>{q.context}</Text>
        </View>
      ),
    },
    {
      key: "frequency",
      flex: 1,
      renderCell: (q) => (
        <View style={{ alignItems: "flex-end" }}>
          <Text style={{ fontWeight: "600" }}>{q.frequency}</Text>
        </View>
      ),
    },
    {
      key: "action",
      flex: 1.5,
      renderCell: (q) => (
        <View style={{ alignItems: "center", justifyContent: "center" }}>
          <CustomButton
            text="Create ILE"
            useTranslationText={false}
            styleType="secondaryLightened"
            size="xs"
            leftIcon={<Sparkle size={14} color={colors.grey[700]} />}
            onPress={() => {}}
            style={{
              paddingHorizontal: 10,
              paddingVertical: 6,
              shadowColor: "transparent",
              elevation: 0,
            }}
            textStyle={{
              fontSize: 12,
              fontWeight: "500",
              color: colors.grey[700],
            }}
          />
        </View>
      ),
    },
  ];

  const counts = useMemo(() => {
    return mappedQuestions.reduce(
      (acc, q) => {
        acc.all += 1;
        if (q.type === "assignment") acc.assignment += 1;
        if (q.type === "course") acc.course += 1;
        return acc;
      },
      { all: 0, assignment: 0, course: 0 }
    );
  }, [mappedQuestions]);

  function TabCount({ value }: { value: number }) {
    return (
      <Text
        style={{
          marginLeft: 8,
          paddingHorizontal: 6,
          paddingVertical: 2,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: "500",
          backgroundColor: "#EAECF0",
          color: "#344054",
        }}>
        {value}
      </Text>
    );
  }
  const rows = useMemo(() => {
    return [...filteredQuestions].sort((a, b) => b.frequency - a.frequency);
  }, [filteredQuestions]);

  return (
    <View>
      {/* Inner Tabs */}
      <Tabs value={filter} onValueChange={handleFilterChange}>
        <View style={{ flexDirection: "row", alignItems: "center", marginBottom: spacing.sm }}>
          <TabsList style={styles.tabsList}>
            <TabsTrigger variant="default" value="all">
              <Text style={styles.tabTriggerText}>All</Text>
              <TabCount value={counts.all} />
            </TabsTrigger>

            <TabsTrigger variant="default" value="assignment">
              <Text style={styles.tabTriggerText}>Assignment</Text>
              <TabCount value={counts.assignment} />
            </TabsTrigger>

            <TabsTrigger variant="default" value="course">
              <Text style={styles.tabTriggerText}>Course</Text>
              <TabCount value={counts.course} />
            </TabsTrigger>
          </TabsList>
        </View>

        <TabsContent value="all">
          {questionsLoading && (
            <View style={styles.loaderContainer}>
              <ActivityIndicator size="large" color="#0000ff" />
            </View>
          )}

          {/* EMPTY STATE */}
          {isQuestionEmpty && (
            <View style={styles.loaderContainer}>
              <CustomText text="Not Available" size="s" style={styles.pendingText} />
            </View>
          )}

          {/* DATA TABLE */}
          {!questionsLoading && !isQuestionEmpty && (
            <DataTable columns={columns} data={rows} keyExtractor={(q) => q.id} />
          )}
        </TabsContent>

        <TabsContent value="assignment">
          {questionsLoading && (
            <View style={styles.loaderContainer}>
              <ActivityIndicator size="large" color="#0000ff" />
            </View>
          )}

          {/* EMPTY STATE */}
          {isQuestionEmpty && (
            <View style={styles.loaderContainer}>
              <CustomText text="Not Available" size="s" style={styles.pendingText} />
            </View>
          )}

          {/* DATA TABLE */}
          {!questionsLoading && !isQuestionEmpty && (
            <DataTable columns={columns} data={rows} keyExtractor={(q) => q.id} />
          )}
        </TabsContent>

        <TabsContent value="course">
          {questionsLoading && (
            <View style={styles.loaderContainer}>
              <ActivityIndicator size="large" color="#0000ff" />
            </View>
          )}

          {/* EMPTY STATE */}
          {isQuestionEmpty && (
            <View style={styles.loaderContainer}>
              <CustomText text="Not Available" size="s" style={styles.pendingText} />
            </View>
          )}

          {/* DATA TABLE */}
          {!questionsLoading && !isQuestionEmpty && (
            <DataTable columns={columns} data={rows} keyExtractor={(q) => q.id} />
          )}
        </TabsContent>
      </Tabs>
    </View>
  );
}
const styles = StyleSheet.create({
  tabsList: {
    marginBottom: spacing.lg,
  },
  tabTriggerText: {},
  loaderContainer: {
    flex: 3,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  pendingText: {
    color: "#6B7280",
    fontStyle: "italic",
  },
});
